import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import { Box, Card, DialogActions, DialogContent, Toolbar, useTheme } from "@mui/material";
import { strings } from "../../../../localization/LocalizedStrings";
import { TextField } from "../../../TextField";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { AlertDialog } from "../../../AlertDialog";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { FieldTitle } from "../../../Text/FieldTitle";
import { ContainedButton } from "../../../ContainedButton";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../../hooks/isMobile";
import { FreeTextListItem } from "../../../FreeTextListItem";
import { ModifyFreeTextAttributeDialog } from "../../ModifyFreeTextAttributeDialog";
import { ITag } from "../../../../hooks/tags";
import { useAddTag } from "../../../../hooks/tags/useAddTag";
import { useModifyTag } from "../../../../hooks/tags/useModifyTag";
import { useDeleteTag } from "../../../../hooks/tags/useDeleteTag";
import { Firebase } from "../../../../services/Firebase";
import { ProductPurchasesDialog } from "../../../products/ProductPurchasesDialog";
import { ProductType } from "../../../../hooks/products/interfaces";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";

export const ModifyTagDialog: FunctionComponent<IModifyTagDialogProps> = ({
  open,
  onClose,
  tag,
  onNewItem,
  onModifiedItem,
  onDeletedItem
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const dialogTitle = tag ? strings.tag_title : strings.new_tag_title;
  const { addTag } = useAddTag();
  const { modifyTag } = useModifyTag();
  const { deleteTag } = useDeleteTag();
  const [isLoading, setIsLoading] = useState(false);
  const [isModifyDescriptionDialogOpen, setIsModifyDescriptionDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [isPurchaseAlertOpen, setIsPurchaseAlertOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onClose();
    }
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.field_missing)
  });
  const formik = useFormik({
    initialValues: {
      name: tag?.name ?? "",
      description: tag?.description ?? ""
    },
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async ({ name, description }) => {
      setIsLoading(true);
      if (tag) {
        const { status, body } = await modifyTag({
          uuid: tag.uuid,
          name: name,
          description: description
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_tag_action", { isNew: false });
          onModifiedItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      } else {
        const { status, body } = await addTag({
          name: name,
          description: description
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_tag_action", { isNew: true });
          onNewItem?.(body);
          onClose();
        } else if (StatusCodesHelper.isLimitReached(status)) {
          setAlertMessage(body.message);
          setIsPurchaseAlertOpen(true);
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  const onDelete = async () => {
    if (!tag) return;
    setIsLoading(true);
    const { status, body } = await deleteTag({ uuid: tag.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      onDeletedItem(tag);
      onClose();
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoading(false);
  };
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"xs"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {tag && (
            <ToolbarButton
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <FieldTitle>{strings.name_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="name"
              name="name"
              size="small"
              autoFocus={!isMobile}
              placeholder={strings.name_title}
              value={formik.values.name}
              onChange={formik.handleChange}
              enterAction={formik.submitForm}
            />
          </div>
          <FieldTitle>{strings.collection_description}</FieldTitle>
          <Card className={styles.profileCard}>
            <FreeTextListItem
              hideTitle
              detail={formik.values.description ?? "--"}
              maxLines={4}
              onClick={() => setIsModifyDescriptionDialogOpen(true)}
            />
          </Card>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <ModifyFreeTextAttributeDialog
        title={strings.collection_description}
        description={strings.edit_description_description}
        placeholder={strings.collection_description}
        value={formik.values.description}
        open={isModifyDescriptionDialogOpen}
        onClose={() => setIsModifyDescriptionDialogOpen(false)}
        onChange={newValue => formik.setFieldValue("description", newValue)}
        onClearValue={() => formik.setFieldValue("description", null)}
      />
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={onClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
      <AlertDialog
        message={alertMessage}
        open={isPurchaseAlertOpen}
        setOpen={setIsPurchaseAlertOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setIsPurchaseDialogOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.TAGS}
        open={isPurchaseDialogOpen}
        setOpen={setIsPurchaseDialogOpen}
      />
    </FormikProvider>
  );
};

export interface IModifyTagDialogProps {
  open: boolean;
  onClose: () => void;
  tag?: ITag;
  onNewItem?: (item: ITag) => void;
  onModifiedItem?: (item: ITag) => void;
  onDeletedItem?: (item: ITag) => void;
}
